import { useContext, useRef, useEffect } from "react";
import { createConsumer } from "@rails/actioncable";
import { RemixBuilderContext } from "./RemixBuilderContext";
import apiRequest from "../../../utils/apiRequest";

const useRemix = (props) => {
  let contextData;
  let builder;
  let setContextData;

  if (props?.builder == true) {
    [contextData, builder, setContextData] = useContext(RemixBuilderContext);
  }

  const contextDataRef = useRef(contextData);
  const websiteId = contextData?.website?.id || props?.website_id;

  useEffect(() => {
    contextDataRef.current = contextData;
  }, [contextData]);

  const Remix = {
    getSectionTemplates: async function (params = {}) {
      let endpoint = `/api/v1/section_templates`;

      if (Object.keys(params).length > 0) {
        endpoint += `?${new URLSearchParams(params).toString()}`;
      }

      const response = await apiRequest("GET", endpoint);

      if (response && response.code >= 200 && response.code < 300 && response.json) {
        return response.json;
      } else {
        console.error('Failed to fetch section templates:', {
          status: response?.code,
          message: response?.message,
          endpoint: endpoint
        });
        return [];
      }
    },

    sortableStart: (containerIdOrElement, contextDataKey, stepPosition = null) => {
      const container =
        typeof containerIdOrElement === "string"
          ? document.querySelector(`#${containerIdOrElement}`)
          : containerIdOrElement;

      if (container) {
        const sortableOptions = {
          sort: true,
          group: { name: 'layout-sections' },
          animation: 150,
          draggable: '.section-selector-box',
          forceFallback: true,
          fallbackOnBody: true,
          fallbackTolerance: 5,
          fallbackClass: 'builder-sortable-fallback',
          scroll: true,
          scrollSensitivity: 100,
          scrollSpeed: 10,
          onUpdate: (event) => {
            Remix.sortableUpdate(event, contextDataKey, stepPosition);
          }
        };

        return new Sortable(container, sortableOptions);
      }
      return null;
    },

    sortableUpdate: (event, contextDataKey, stepPosition = null) => {
      const sections = Array.from(event.to.children);

      const newPositionsMap = sections.reduce((acc, section, index) => {
        acc[section.getAttribute('data-section-id')] = index + 1;
        return acc;
      }, {});

      const currentSections = contextDataRef.current[contextDataKey];
      const normalizedStepPosition = Number.isFinite(parseInt(stepPosition, 10))
        ? parseInt(stepPosition, 10)
        : null;

      const updatedSections = currentSections
        .map((section) => ({
          ...section,
          position:
            normalizedStepPosition === null ||
            (section.step_position || 1) === normalizedStepPosition
              ? (newPositionsMap[section.id] || section.position)
              : section.position
        }))
        .sort((a, b) => {
          const stepA = a.step_position || 1;
          const stepB = b.step_position || 1;
          if (stepA !== stepB) return stepA - stepB;
          return (a.position || 0) - (b.position || 0);
        });

      setContextData((prevContextData) => ({
        ...prevContextData,
        matched_sections: updatedSections
      }));
    },

    parsedSubmitPayload: function () {
      const remixSettings = contextData.remix_settings;
      const rawExcluded = remixSettings.options?.excluded_element_types;
      const excluded_element_types = Array.isArray(rawExcluded)
        ? rawExcluded.map((o) => (typeof o === "string" ? o : o?.value)).filter(Boolean)
        : [];
      const processedOptions = {
        ...remixSettings.options,
        remix_all_text: remixSettings.options?.remix_all_text === true ? "true" : "false",
        excluded_element_types,
      };

      const ctaTypeValue = remixSettings.cta_type?.value ?? remixSettings.cta_type;

      let payload = {
        cta_id: contextData.cta?.id,
        settings: {
          ...remixSettings,
          cta_type: ctaTypeValue,
          options: processedOptions,
        }
      }

      if (remixSettings.styles?.["heading-font-family"]) {
        const headingFont = remixSettings.styles["heading-font-family"];
        payload.settings.styles["heading-font-family"] =
          headingFont?.value ?? headingFont;
      }

      if (remixSettings.styles?.["paragraph-font-family"]) {
        const paragraphFont = remixSettings.styles["paragraph-font-family"];
        payload.settings.styles["paragraph-font-family"] =
          paragraphFont?.value ?? paragraphFont;
      }

      payload["section_templates"] = contextData.matched_sections
        .filter((section) => section.selected == true)
        .map((section) => {
          const stepPosition = section.step_position || 1;
          const stepName = contextData.funnel_step_names?.[stepPosition];
          return {
            step_position: stepPosition,
            step_name: stepName,
            position: section.position,
            template_id: section.template?.id,
            remix_tone:
              typeof section.remix_tone === "string"
                ? section.remix_tone.trim()
                : "",
          };
        });

      payload["funnel_steps"] = Object.keys(contextData.funnel_step_names || {})
        .map((stepPosition) => ({
          step_position: parseInt(stepPosition, 10),
          step_name: contextData.funnel_step_names[stepPosition],
        }))
        .filter((step) => Number.isFinite(step.step_position));

      return payload;
    },

    promptContent: async function (content) {
      const response = await apiRequest("POST", `/websites/${websiteId}/remix/content`, { content });

      if (response && response.json) {
        return response.json;
      }
    },

    createCTA: async function (cableSyncId = null) {
      const payload = this.parsedSubmitPayload();
      if (cableSyncId) payload.cable_sync_id = cableSyncId;
      const response = await apiRequest("POST", `/websites/${websiteId}/remix/cta`, payload);

      if (response && response.json) {
        return response.json;
      } else {
        return null;
      }
    },

    createVariant: async function (cableSyncId = null) {
      const payload = this.parsedSubmitPayload();
      if (cableSyncId) payload.cable_sync_id = cableSyncId;
      const response = await apiRequest("POST", `/websites/${websiteId}/remix/variant`, payload);

      if (response && response.json) {
        return response.json;
      } else {
        return null;
      }
    },

    importNativeFromImage: function (
      imageURL,
      campaignName = "Imported Campaign",
      onProgress = null
    ) {
      return new Promise((resolve, reject) => {
        if (!imageURL) {
          reject(new Error("Image URL is required"));
          return;
        }

        const cableSyncId = Math.random().toString(36).substring(2, 15);
        const consumer = createConsumer();
        let subscription;
        const cleanup = () => {
          if (subscription) subscription.unsubscribe();
          consumer.disconnect();
        };

        subscription = consumer.subscriptions.create(
          { channel: "AsyncModalRequestConfirmationChannel", id: cableSyncId },
          {
            received: (data) => {
              if (data.status === "ready" && data.builder_url) {
                cleanup();
                window.location.href = data.builder_url;
                resolve(data);
                return;
              }
              if (data.status === "failed") {
                cleanup();
                reject(new Error(data.error || "Import failed"));
                return;
              }
              if (data.status === "progress" && typeof onProgress === "function") {
                onProgress(data);
              }
            },
            rejected: () => {
              cleanup();
              reject(new Error("Unable to establish a connection"));
            },
          }
        );

        apiRequest(
          "POST",
          `/websites/${websiteId}/remix/cta`,
          {
            import_mode: "json",
            cable_sync_id: cableSyncId,
            settings: {
              campaign_name: campaignName,
              cta_type: "page",
            },
            asset_params: {
              image_url: imageURL,
            },
          },
          null,
          {
            "Content-Type": "application/json",
            Accept: "application/json",
          }
        ).then((response) => {
          if (!response || response.code < 200 || response.code >= 300) {
            cleanup();
            reject(new Error(response?.message || "Import failed"));
          }
        }).catch((error) => {
          cleanup();
          reject(error);
        });
      });
    },

    transformSectionTagsForModal: (sectionTags) => {
      if (!Array.isArray(sectionTags)) return {};

      const modalFilters = {};

      const categoryMapping = {
        'other': 'element',
        'goal': 'goal',
        'industry': 'industry'
      };

      sectionTags.forEach(tag => {
        const mappedCategory = categoryMapping[tag.tag_type];
        const finalCategory = mappedCategory || tag.tag_type;

        if (!modalFilters[finalCategory]) {
          modalFilters[finalCategory] = {};
        }

        modalFilters[finalCategory][tag.name] = true;
      });

      return modalFilters;
    },
  };

  return { Remix };
};

export default useRemix;
